// pages/ClaudeActivity.jsx — Claude (claude.ai org) member activity from OpenTelemetry
// Backend: backend/claudeOtel.js
//   GET /api/claude-otel/dashboard?days=N   aggregates (admin only)
//   GET /api/claude-otel/events?session=…   event timeline of one session
//   GET /api/claude-otel/status             receiver health
// Source: Claude Admin settings → Data and privacy → Monitoring (Cowork / unified chat),
// OTLP http/json → https://indomas-it.duckdns.org/api/otel. Prompt and response text are
// redacted by Claude; only metadata (who, session, tool, model, tokens, cost) is stored.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { api } from '../context/AuthContext';

const DAY_OPTIONS = [1, 7, 14, 30, 90, 180];

const fmtTs = (ms) => {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};
const fmtUsd = (v) => `$${(Number(v) || 0).toFixed((Number(v) || 0) < 1 ? 4 : 2)}`;
const fmtNum = (v) => (Number(v) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const fmtTok = (v) => {
  const n = Number(v) || 0;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
};
const fmtDur = (a, b) => {
  if (!a || !b) return '—';
  const s = Math.max(0, Math.round((b - a) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};
const shortUser = (u) => String(u || '').replace(/@pacificindomas\.com$/i, '');

// ── Overlay: side drawer or centred modal (choice remembered per browser) ──
const MODE_KEY = 'claudeActivity.modalMode';
function useModalMode() {
  const [mode, setMode] = useState(() => { try { return localStorage.getItem(MODE_KEY) || 'side'; } catch (_) { return 'side'; } });
  const toggle = () => setMode((m) => { const n = m === 'side' ? 'modal' : 'side'; try { localStorage.setItem(MODE_KEY, n); } catch (_) {} return n; });
  return [mode, toggle];
}
function ModeToggle({ mode, onToggle }) {
  return (
    <button onClick={onToggle} title={mode === 'side' ? 'Switch to centred modal' : 'Switch to side panel'}
      className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs text-gray-600 hover:bg-gray-50">
      {mode === 'side' ? '⧉ Modal' : '◧ Side'}
    </button>
  );
}
function Overlay({ mode, onClose, children, z = 'z-50' }) {
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  const side = mode === 'side';
  const [off, setOff] = useState({ x: 0, y: 0 });
  // Drag the modal by its white header bar (buttons/inputs excluded). Resize via the native corner grip.
  const startDrag = (e) => {
    if (side || e.button !== 0) return;
    if (e.target.closest('button,select,input,a,textarea')) return;
    if (!e.target.closest('.bg-white.border-b')) return;
    e.preventDefault();
    const sx = e.clientX - off.x; const sy = e.clientY - off.y;
    const mv = (ev) => setOff({ x: ev.clientX - sx, y: Math.max(-window.innerHeight / 2 + 40, ev.clientY - sy) });
    const up = () => { window.removeEventListener('mousemove', mv); window.removeEventListener('mouseup', up); document.body.style.userSelect = ''; };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', mv); window.addEventListener('mouseup', up);
  };
  const modalStyle = side ? { resize: 'horizontal', direction: 'rtl', width: 'min(48rem, 100vw)', minWidth: 420, maxWidth: '95vw' } : {
    transform: `translate(${off.x}px, ${off.y}px)`, resize: 'both', width: 'min(48rem, 95vw)', height: '85vh',
    minWidth: 420, minHeight: 300, maxWidth: '98vw', maxHeight: '98vh',
  };
  return (
    <div className={`fixed inset-0 ${z} flex bg-black/30 ${side ? 'justify-end' : 'items-center justify-center p-4'}`} onClick={onClose}>
      <div className={`flex flex-col overflow-hidden bg-gray-50 shadow-xl ${side ? 'h-full' : 'rounded-2xl'} ${side ? '' : '[&_.bg-white.border-b]:cursor-move'}`}
        style={modalStyle} onMouseDown={startDrag} onClick={(e) => e.stopPropagation()}>
        <div className="flex min-h-0 flex-1 flex-col" style={side ? { direction: 'ltr' } : undefined}>{children}</div>
      </div>
    </div>
  );
}

// ── Light markdown renderer for transcript text (code fences, headings, lists, bold, inline code) ──
function inlineMd(text, mark) {
  const parts = String(text).split(/(`[^`]+`|\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (/^`[^`]+`$/.test(p)) return <code key={i} className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.85em] text-rose-700">{p.slice(1, -1)}</code>;
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}>{mark(p.slice(2, -2))}</strong>;
    return <React.Fragment key={i}>{mark(p)}</React.Fragment>;
  });
}
// The claude.ai export replaces non-text blocks (thinking, tool calls/results, artifacts) with a fixed
// placeholder sentence, often wrapped in ``` fences. Collapse each run of them into one muted label.
const EXPORT_PLACEHOLDER_RUN = /(?:\s*(?:```[^\n]*\n)?\s*This block is not supported on your current device yet\.?\s*(?:```)?\s*)+/g;
function AssistantText({ text, mark }) {
  const parts = [];
  let last = 0;
  String(text).replace(EXPORT_PLACEHOLDER_RUN, (match, offset) => {
    const n = (match.match(/This block is not supported/g) || []).length;
    if (offset > last) parts.push({ t: text.slice(last, offset) });
    parts.push({ ph: n });
    last = offset + match.length;
    return match;
  });
  if (last < text.length) parts.push({ t: text.slice(last) });
  return (
    <>
      {parts.map((p, i) => (p.ph
        ? <div key={i} className="my-1.5 inline-flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 bg-gray-50 px-2 py-1 text-[11px] text-gray-500"
            title="The claude.ai data export does not include thinking, tool calls/results or artifacts — only plain text.">
            ⚙️ {p.ph > 1 ? `${p.ph} steps` : '1 step'} — thinking / tool call (not included in export)
          </div>
        : p.t.trim() ? <Markdown key={i} text={p.t} mark={mark} /> : null))}
    </>
  );
}

function Markdown({ text, mark }) {
  const out = []; const lines = String(text).split('\n'); let i = 0; let key = 0;
  while (i < lines.length) {
    const l = lines[i];
    const fence = l.match(/^```(\S*)/);
    if (fence) {
      const buf = []; i++;
      while (i < lines.length && !/^```/.test(lines[i])) { buf.push(lines[i]); i++; }
      i++;
      out.push(
        <div key={key++} className="my-2 overflow-hidden rounded-lg border border-gray-200">
          {fence[1] && <div className="border-b border-gray-200 bg-gray-100 px-3 py-1 font-mono text-[11px] text-gray-500">{fence[1]}</div>}
          <pre className="overflow-x-auto bg-gray-900 p-3 text-xs leading-relaxed text-gray-100"><code>{buf.join('\n')}</code></pre>
        </div>);
      continue;
    }
    const h = l.match(/^(#{1,4})\s+(.*)/);
    if (h) { out.push(<div key={key++} className={`mt-3 mb-1 font-semibold text-gray-900 ${h[1].length <= 2 ? 'text-base' : 'text-sm'}`}>{inlineMd(h[2], mark)}</div>); i++; continue; }
    if (/^\s*([-*]|\d+\.)\s+/.test(l)) {
      const items = []; const ordered = /^\s*\d+\./.test(l);
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*([-*]|\d+\.)\s+/, '')); i++; }
      const Tag = ordered ? 'ol' : 'ul';
      out.push(<Tag key={key++} className={`my-1 space-y-0.5 pl-5 ${ordered ? 'list-decimal' : 'list-disc'}`}>{items.map((x, j) => <li key={j}>{inlineMd(x, mark)}</li>)}</Tag>);
      continue;
    }
    if (!l.trim()) { i++; continue; }
    const para = [];
    while (i < lines.length && lines[i].trim() && !/^```|^#{1,4}\s|^\s*([-*]|\d+\.)\s+/.test(lines[i])) { para.push(lines[i]); i++; }
    out.push(<p key={key++} className="my-1.5 whitespace-pre-wrap break-words">{inlineMd(para.join('\n'), mark)}</p>);
  }
  return <>{out}</>;
}

function Kpi({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-gray-800">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

function Card({ title, right, children }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-2.5">
        <span className="font-semibold text-gray-800">{title}</span>
        {right && <span className="ml-auto text-xs text-gray-500">{right}</span>}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function Table({ head, rows, empty = 'No data for this period.' }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
          <tr>{head.map((h) => <th key={h.k} className={`px-3 py-2 ${h.r ? 'text-right' : ''}`}>{h.k}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.length ? rows : <tr><td colSpan={head.length} className="px-3 py-6 text-center text-gray-400">{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function eventDetail(e) {
  let a = {};
  try { a = JSON.parse(e.attrs || '{}'); } catch (_) { /* ignore */ }
  switch (e.event_name) {
    case 'user_prompt': return `prompt · ${a.prompt_length || '?'} chars`;
    case 'api_request': return `${a.model || ''} · in ${fmtTok(a.input_tokens)} / out ${fmtTok(a.output_tokens)} / cache ${fmtTok(a.cache_read_tokens)} · ${fmtUsd(a.cost_usd)} · ${a.duration_ms || '?'} ms${a.query_source ? ` · ${a.query_source}` : ''}`;
    case 'api_error': return `${a.model || ''} · ${a.status_code || ''} ${a.error || ''}`;
    case 'tool_decision': return `${a.tool_name || ''} → ${a.decision || ''} (${a.source || ''})`;
    case 'tool_result': return `${a.tool_name || ''} · ${String(a.success) === 'false' ? 'FAILED' : 'ok'} · ${a.duration_ms || '?'} ms`;
    case 'assistant_response': return `response · ${a.response_length || '?'} chars · ${a.model || ''}`;
    case 'mcp_server_connection': return `${a.status || ''} · ${a.transport_type || ''} · ${a.server_scope || ''}`;
    case 'plugin_loaded': return a['plugin.name'] || '';
    default: return '';
  }
}

// ── Panel detail satu member (chat export + Cowork OTel) ───────────────────
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function MemberDetail({ email, days, estCost, onClose, onFilter }) {
  const [mode, toggleMode] = useModalMode();
  const [readUuid, setReadUuid] = useState(null);
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true; setD(null); setErr('');
    api.get(`/api/claude-otel/member?email=${encodeURIComponent(email)}&days=${days}`)
      .then((r) => { if (alive) setD(r.data); })
      .catch((e) => { if (alive) setErr((e.response && e.response.data && e.response.data.error) || e.message); });
    return () => { alive = false; };
  }, [email, days]);
  const c = (d && d.chat) || {}; const t = (d && d.team) || {};
  const ratio = (v, a) => (a ? `${((Number(v) || 0) / a).toFixed(1)}× team avg` : '');
  const hours = Array.from({ length: 24 }, (_, h) => ({ hour: String(h).padStart(2, '0'), prompts: ((d && d.hourly) || []).find((x) => x.hour === h)?.prompts || 0 }));
  const peak = hours.reduce((m, x) => (x.prompts > m.prompts ? x : m), { hour: '—', prompts: 0 });
  const week = DOW.map((n, i) => ({ day: n, prompts: ((d && d.weekday) || []).find((x) => x.dow === i)?.prompts || 0 }));
  const typedPct = c.prompts ? Math.round(((c.typed_prompts || 0) / c.prompts) * 100) : 0;
  const cw = (d && d.cowork) || {};
  return (
    <Overlay mode={mode} onClose={readUuid ? () => {} : onClose}>
      <div className="flex-1 overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-gray-200 bg-white px-5 py-3">
          <div className="min-w-0">
            <div className="truncate text-lg font-bold text-gray-800">{(d && d.name) || shortUser(email)}</div>
            <div className="truncate text-xs text-gray-500">{email} · {periodText(days)}</div>
          </div>
          <button onClick={() => { onFilter(email); onClose(); }} className="ml-auto rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">Show conversations</button>
          <ModeToggle mode={mode} onToggle={toggleMode} />
          <button onClick={onClose} className="rounded-lg px-2 py-1 text-gray-500 hover:bg-gray-100">✕</button>
        </div>
        {err && <div className="m-5 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{err}</div>}
        {!d && !err && <div className="p-8 text-center text-sm text-gray-500">Loading…</div>}
        {d && (
          <div className="space-y-4 p-5">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Kpi label="Conversations" value={fmtNum(c.conversations)} sub={`#${t.rank_conversations} of ${t.members} · ${ratio(c.conversations, t.avg_conversations)}`} />
              <Kpi label="Prompts" value={fmtNum(c.prompts)} sub={`#${t.rank_prompts} of ${t.members} · ${ratio(c.prompts, t.avg_prompts)}`} />
              <Kpi label="Typed prompts" value={fmtNum(c.typed_prompts)} sub={`${typedPct}% of prompts have text`} />
              <Kpi label="Active days" value={fmtNum(c.active_days)} sub={ratio(c.active_days, t.avg_active_days)} />
              <Kpi label="Avg prompt length" value={`${fmtNum(c.avg_prompt_chars)} ch`} />
              <Kpi label="Attachments" value={fmtNum(c.attachments)} />
              <Kpi label="Est. tokens (in / out)" value={`${fmtTok(c.est_in)} / ${fmtTok(c.est_out)}`} sub="upper bound" />
              <Kpi label="Est. API-equiv. cost" value={fmtUsd(estCost(c.est_in, c.est_out))} sub={`team avg ${fmtUsd(estCost(t.avg_est_in, t.avg_est_out))}`} />
            </div>
            {d.combined && (
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="mb-2 font-semibold text-gray-800">🔗 Chat + Cowork combined</div>
                <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                  <div><div className="text-xs text-gray-500">Typed prompts (chat)</div><div className="font-bold">{fmtNum(d.combined.chat_typed_prompts)}</div></div>
                  <div><div className="text-xs text-gray-500">Prompts (Cowork)</div><div className="font-bold">{fmtNum(d.combined.cowork_prompts)}</div></div>
                  <div><div className="text-xs text-gray-500">Total prompts</div><div className="font-bold">{fmtNum(d.combined.total_prompts)}</div></div>
                  <div><div className="text-xs text-gray-500">Total cost (chat est. + Cowork)</div><div className="font-bold">{fmtUsd(estCost(d.combined.chat_est_in, d.combined.chat_est_out) + d.combined.cowork_cost)}</div></div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  {d.combined.duplicates_removed ? `${d.combined.duplicates_removed} export conversation(s) matched a Cowork session and were excluded. ` : 'No export conversation matches a Cowork session ID. '}
                  {d.combined.time_overlaps ? `${d.combined.time_overlaps} conversation(s) overlap in time with a Cowork session — review.` : 'No time overlap.'}
                </div>
              </div>
            )}
            <div className="text-xs text-gray-500">Chat active {fmtTs(c.first_ts)} → {fmtTs(c.last_ts)}.
              {cw.sessions ? ` Cowork (live telemetry): ${fmtNum(cw.sessions)} sessions, ${fmtNum(cw.prompts)} prompts, ${fmtNum(cw.tool_calls)} tool calls, ${fmtUsd(cw.cost)} est. cost, last ${fmtTs(cw.last_ts)}.` : ' No Cowork telemetry for this member in the period.'}
            </div>

            <Card title="📅 Prompts per month" right="typed = prompts with text">
              <div style={{ width: '100%', height: 200 }}>
                <ResponsiveContainer>
                  <BarChart data={d.monthly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={40} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="prompts" name="All prompts" fill="rgb(var(--brand-300))" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="typed" name="Typed" fill="rgb(var(--brand-700))" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card title="💲 Cost per month" right={`Chat = text-only estimate · Cowork = actual cost${d.telemetrySince ? ` (telemetry since ${new Date(d.telemetrySince + 7 * 3600000).toISOString().slice(0, 10)})` : ' (telemetry not active)'}`}>
              {(d.monthlyCost || []).length ? (
                <>
                  <div className="mb-2 rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Chat cost is estimated from message text only (characters ÷ 4). It excludes attachments, tool results and browser screenshots, so it understates chats that rely on tools or Claude in Chrome. It is not comparable with Cowork cost, and a drop in chat cost may mean work moved to Cowork rather than became cheaper.
                  </div>
                  <div style={{ width: '100%', height: 200 }}>
                    <ResponsiveContainer>
                      <BarChart data={(d.monthlyCost || []).map((m) => ({ month: m.month, chat: +estCost(m.chat_est_in, m.chat_est_out).toFixed(2), cowork: +m.cowork_cost.toFixed(2) }))}
                        margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f3" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} width={48} tickFormatter={(v) => `$${v}`} />
                        <Tooltip formatter={(v) => fmtUsd(v)} />
                        <Bar dataKey="chat" name="Chat (text-only estimate)" stackId="c" fill="rgb(var(--brand-300))" />
                        <Bar dataKey="cowork" name="Cowork (actual)" stackId="c" fill="rgb(var(--brand-700))" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <Table head={[{ k: 'Month' }, { k: 'Chat est. tokens (in / out)', r: 1 }, { k: 'Chat est. cost', r: 1 }, { k: 'Cowork actual cost', r: 1 }, { k: 'Total (mixed)', r: 1 }]}
                    rows={(d.monthlyCost || []).map((m) => {
                      const cc = estCost(m.chat_est_in, m.chat_est_out);
                      const tMonth = d.telemetrySince ? new Date(d.telemetrySince + 7 * 3600000).toISOString().slice(0, 7) : null;
                      const noTel = !tMonth || m.month < tMonth;
                      return (
                        <tr key={m.month}>
                          <td className="px-3 py-1.5">{m.month}</td>
                          <td className="px-3 py-1.5 text-right">{fmtTok(m.chat_est_in)} / {fmtTok(m.chat_est_out)}</td>
                          <td className="px-3 py-1.5 text-right">{fmtUsd(cc)}</td>
                          <td className="px-3 py-1.5 text-right" title={noTel ? 'Telemetry was not active in this month' : ''}>{noTel ? <span className="text-gray-400">— no telemetry</span> : fmtUsd(m.cowork_cost)}</td>
                          <td className="px-3 py-1.5 text-right font-semibold">{fmtUsd(cc + m.cowork_cost)}</td>
                        </tr>
                      );
                    })} />
                </>
              ) : <div className="py-6 text-center text-sm text-gray-400">No data for this period.</div>}
            </Card>

            <div className="grid gap-4 md:grid-cols-2">
              <Card title="🕘 By hour (WIB)" right={peak.prompts ? `peak ${peak.hour}:00` : ''}>
                <div style={{ width: '100%', height: 170 }}>
                  <ResponsiveContainer>
                    <BarChart data={hours} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                      <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={2} />
                      <YAxis tick={{ fontSize: 10 }} width={34} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="prompts" name="Prompts" fill="rgb(var(--brand-500))" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
              <Card title="📆 By weekday">
                <div style={{ width: '100%', height: 170 }}>
                  <ResponsiveContainer>
                    <BarChart data={week} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} width={34} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="prompts" name="Prompts" fill="rgb(var(--brand-500))" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>

            <Card title="🏷️ Topics" right="from conversation titles">
              <div className="space-y-1.5">
                {d.topics.map((x) => {
                  const max = d.topics[0] ? d.topics[0].conversations : 1;
                  return (
                    <div key={x.topic} className="flex items-center gap-2 text-xs">
                      <div className="w-44 shrink-0 truncate text-gray-700">{x.topic}</div>
                      <div className="h-3 flex-1 rounded bg-gray-100"><div className="h-3 rounded" style={{ width: `${(x.conversations / max) * 100}%`, backgroundColor: 'rgb(var(--brand-500))' }} /></div>
                      <div className="w-24 shrink-0 text-right text-gray-600">{x.conversations} conv · {fmtNum(x.prompts)} pr</div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card title="🔥 Heaviest conversations" right="by prompts">
              <Table head={[{ k: 'Title' }, { k: 'Topic' }, { k: 'Last' }, { k: 'Prompts', r: 1 }, { k: 'Est. cost', r: 1 }]}
                rows={d.topConversations.map((x) => (
                  <tr key={x.uuid} className="cursor-pointer hover:bg-gray-50" onClick={() => setReadUuid(x.uuid)}>
                    <td className="px-3 py-1.5 max-w-xs truncate" title={x.title || ''} style={{ color: 'rgb(var(--brand-700))' }}>{x.title || <span className="text-gray-400">(untitled)</span>}</td>
                    <td className="px-3 py-1.5 text-xs text-gray-600 whitespace-nowrap">{x.topic}</td>
                    <td className="px-3 py-1.5 text-xs text-gray-600 whitespace-nowrap">{fmtTs(x.last_msg_ts)}</td>
                    <td className="px-3 py-1.5 text-right">{fmtNum(x.human_msgs)}</td>
                    <td className="px-3 py-1.5 text-right">{x.est_out_tokens == null ? '—' : fmtUsd(estCost(x.est_in_tokens, x.est_out_tokens))}</td>
                  </tr>
                ))} />
            </Card>
            <div className="text-xs text-gray-400">Click a conversation to open its detail.</div>
          </div>
        )}
      </div>
      {readUuid && <Transcript uuid={readUuid} onClose={() => setReadUuid(null)} z="z-[60]" />}
    </Overlay>
  );
}

// ── Transkrip satu percakapan (isi pesan hanya ada bila disimpan saat impor) ──
function Transcript({ uuid, onClose, highlight, z }) {
  const [mode, toggleMode] = useModalMode();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true; setD(null); setErr('');
    api.get(`/api/claude-otel/conversation/${encodeURIComponent(uuid)}`)
      .then((r) => { if (alive) setD(r.data); })
      .catch((e) => { if (alive) setErr((e.response && e.response.data && e.response.data.error) || e.message); });
    return () => { alive = false; };
  }, [uuid]);
  const mark = (text) => {
    if (!highlight || !text || typeof text !== 'string') return text;
    const i = text.toLowerCase().indexOf(highlight.toLowerCase());
    if (i < 0) return text;
    return <>{text.slice(0, i)}<mark className="rounded bg-amber-200 px-0.5">{text.slice(i, i + highlight.length)}</mark>{text.slice(i + highlight.length)}</>;
  };
  const c = d && d.conversation;
  return (
    <Overlay mode={mode} onClose={onClose} z={z || 'z-50'}>
      <div className="flex items-center gap-3 border-b border-gray-200 bg-white px-5 py-3">
        <div className="min-w-0">
          <div className="truncate text-base font-semibold text-gray-800">{(c && c.title) || '(untitled)'}</div>
          <div className="truncate text-xs text-gray-500">{c ? `${c.user} · ${c.topic || ''}${c.project_name ? ` · 📁 ${c.project_name}` : ''} · ${fmtTs(c.first_msg_ts)} → ${fmtTs(c.last_msg_ts)}` : ''}</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <ModeToggle mode={mode} onToggle={toggleMode} />
          <button onClick={onClose} className="rounded-lg px-2 py-1 text-gray-500 hover:bg-gray-100">✕</button>
        </div>
      </div>
      <div className="border-b border-amber-200 bg-amber-50 px-5 py-1.5 text-xs text-amber-800">Confidential — opening this conversation is recorded in the Activity Log. Secrets were masked at import.</div>
      <div className="flex-1 overflow-y-auto bg-[#faf9f5]">
        <div className="mx-auto max-w-2xl space-y-6 px-5 py-6">
          {err && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{err}</div>}
          {!d && !err && <div className="p-8 text-center text-sm text-gray-500">Loading…</div>}
          {d && !d.hasText && <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">Message text was not stored for this conversation. Re-import the export with <b>Store message text</b> checked to read it here.</div>}
          {d && d.messages.length === 0 && <div className="text-center text-sm text-gray-400">No messages.</div>}
          {d && d.messages.map((m) => {
            const me = m.sender === 'human';
            const body = m.text != null
              ? (me ? <div className="whitespace-pre-wrap break-words">{mark(m.text)}</div> : <AssistantText text={m.text} mark={mark} />)
              : <div className="italic text-gray-400">{m.chars ? `(${fmtNum(m.chars)} characters — text not stored)` : '(no text — tool result or attachment only)'}</div>;
            if (me) return (
              <div key={m.uuid} className="flex justify-end">
                <div className="max-w-[85%]">
                  {m.attachments ? <div className="mb-1 flex justify-end"><span className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-[11px] text-gray-600">📎 {m.attachments} attachment(s)</span></div> : null}
                  <div className="flex items-start gap-2 rounded-2xl bg-[#f0eee6] px-4 py-2.5 text-[15px] leading-relaxed text-gray-900">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-700 text-[11px] font-semibold uppercase text-white">{shortUser(c && c.user).slice(0, 1)}</span>
                    <div className="min-w-0">{body}</div>
                  </div>
                  <div className="mt-1 text-right text-[11px] text-gray-400">{fmtTs(m.ts)}</div>
                </div>
              </div>
            );
            return (
              <div key={m.uuid} className="flex items-start gap-3">
                <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-sm text-white" style={{ backgroundColor: '#d97757' }}>✳</span>
                <div className="min-w-0 flex-1 text-[15px] leading-relaxed text-gray-800">
                  {body}
                  <div className="mt-1 text-[11px] text-gray-400">Claude · {fmtTs(m.ts)}{m.attachments ? ` · 📎 ${m.attachments}` : ''}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Overlay>
  );
}

// ── Chat klasik dari file Export data (metadata saja) ───────────────────────
function ChatExportPanel({ days }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [manifest, setManifest] = useState(null);
  const [storeText, setStoreText] = useState(false);
  const [textStat, setTextStat] = useState(null);
  const [readUuid, setReadUuid] = useState(null);
  const [sq, setSq] = useState('');
  const [sHits, setSHits] = useState(null);
  const [sBusy, setSBusy] = useState(false);
  const [sErr, setSErr] = useState('');
  const loadTextStat = useCallback(() => { api.get('/api/claude-otel/chat-text/status').then((r) => setTextStat(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadTextStat(); }, [loadTextStat]);
  const runSearch = async () => {
    setSBusy(true); setSErr(''); setSHits(null);
    try { const r = await api.get(`/api/claude-otel/chat-search?q=${encodeURIComponent(sq.trim())}&days=${days}`); setSHits(r.data.rows || []); }
    catch (e) { setSErr((e.response && e.response.data && e.response.data.error) || e.message); }
    finally { setSBusy(false); }
  };
  const purgeText = async () => {
    if (!window.confirm('Delete ALL stored message text? Metadata (counts, titles, topics, cost) stays.')) return;
    try { const r = await api.delete('/api/claude-otel/chat-text'); setMsg(`Deleted ${fmtNum(r.data.deleted)} stored message text(s).`); setSHits(null); loadTextStat(); }
    catch (e) { setError((e.response && e.response.data && e.response.data.error) || e.message); }
  };
  const [topicFilter, setTopicFilter] = useState('all');
  const [topicCell, setTopicCell] = useState(null); // { topic, user|null } → conversation list modal
  const [detail, setDetail] = useState(null);
  const [priceIn, setPriceIn] = useState(() => { try { return Number(localStorage.getItem('claudeChat.priceIn')) || 3; } catch (_) { return 3; } });
  const [priceOut, setPriceOut] = useState(() => { try { return Number(localStorage.getItem('claudeChat.priceOut')) || 15; } catch (_) { return 15; } });
  const savePrice = (k, v, set) => { set(v); try { localStorage.setItem(k, String(v)); } catch (_) {} };
  // Cache-aware estimate: re-sent history is billed as cache reads (default 10% of input price);
  // new content enters the cache once (cache write ≈ 1.25× input, approximated by output tokens).
  const [cachePct, setCachePct] = useState(() => { try { const v = localStorage.getItem('claudeChat.cachePct'); return v == null ? 10 : Number(v); } catch (_) { return 10; } });
  const estCost = (i, o) => ((Number(i) || 0) * priceIn * (cachePct / 100) + (Number(o) || 0) * (priceOut + priceIn * 1.25)) / 1e6;
  const fileRef = React.useRef(null);

  const load = useCallback(async () => {
    setError('');
    try { const r = await api.get(`/api/claude-otel/chat-summary?days=${days}`); setData(r.data); }
    catch (e) { setError((e.response && e.response.data && e.response.data.error) || e.message); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const upload = async (fileList) => {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    setBusy(true); setMsg(''); setError('');
    try {
      let isManifest = false;
      if (list.length === 1 && /\.json$/i.test(list[0].name) && list[0].size < 1024 * 1024) {
        try { const j = JSON.parse(await list[0].text()); isManifest = !!(j && Array.isArray(j.data_files)); } catch (_) { /* not JSON */ }
      }
      if (isManifest) {
        // Link export dilindungi Cloudflare + sesi login claude.ai → server tidak bisa
        // mengunduhnya. Tampilkan tombol unduh untuk dibuka di browser user.
        const j = JSON.parse(await list[0].text());
        setManifest({ created: j.created_at, files: (j.data_files || []).filter((d) => ['conversations', 'light_metadata'].includes(d.category)) });
        return;
      }
      // Kirim per potongan 5 MB: nginx di depan dashboard menolak body besar (HTTP 413).
      const CHUNK = 5 * 1024 * 1024;
      const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      const totalBytes = list.reduce((n, f) => n + f.size, 0);
      let sent = 0;
      for (const f of list) {
        const total = Math.max(1, Math.ceil(f.size / CHUNK));
        for (let i = 0; i < total; i++) {
          const blob = f.slice(i * CHUNK, Math.min(f.size, (i + 1) * CHUNK));
          let tries = 0;
          for (;;) {
            try {
              await api.post(`/api/claude-otel/chat-upload-chunk?id=${id}&name=${encodeURIComponent(f.name)}&idx=${i}&total=${total}&offset=${i * CHUNK}`,
                blob, { headers: { 'Content-Type': 'application/octet-stream' }, timeout: 300000 });
              break;
            } catch (err) { tries += 1; if (tries >= 3) throw err; await new Promise((r) => setTimeout(r, 1500)); }
          }
          sent += blob.size;
          setMsg(`Uploading ${f.name}… ${Math.round((sent / totalBytes) * 100)}% (${(sent / 1048576).toFixed(1)} / ${(totalBytes / 1048576).toFixed(1)} MB)`);
        }
      }
      setMsg('Upload complete — importing on the server…');
      const r = await api.post('/api/claude-otel/chat-import-uploaded', { id, storeText }, { timeout: 900000 });
      setManifest(null);
      const d = r.data;
      setMsg(`Imported ${fmtNum(d.conversations)} conversations, ${fmtNum(d.messages)} messages from ${d.users} member(s)` +
        (d.minTs ? ` (${fmtTs(d.minTs)} → ${fmtTs(d.maxTs)})` : '') + (d.usersFileFound ? '' : ' — no user list found (add light_metadata-000.zip), members shown by account ID') + (d.textStored ? ` · ${fmtNum(d.textStored)} message texts stored (secrets masked)` : ''));
      loadTextStat();
      await load();
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.error) || e.message);
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const k = (data && data.kpi) || {};
  const last = data && data.imports && data.imports[0];
  const convs = data ? data.conversations.filter((c) => (userFilter === 'all' || c.user === userFilter) && (topicFilter === 'all' || (c.topic || 'Untitled') === topicFilter)) : [];
  const parseObj = (v) => { try { return JSON.parse(v || '{}') || {}; } catch (_) { return {}; } };
  const parseArr = (v) => { try { const a = JSON.parse(v || '[]'); return Array.isArray(a) ? a : []; } catch (_) { return []; } };
  const topicMatrix = useMemo(() => {
    if (!data || !data.byTopic) return { topics: [], users: [], cell: {} };
    const cell = {}; const tTot = {}; const users = new Set();
    data.byTopic.forEach((r) => { cell[`${r.topic}|${r.user}`] = r.conversations; tTot[r.topic] = (tTot[r.topic] || 0) + r.conversations; users.add(r.user); });
    const topics = Object.keys(tTot).sort((a, b) => tTot[b] - tTot[a]);
    return { topics, users: Array.from(users).sort(), cell, tTot };
  }, [data]);
  const sumJson = (rows, field) => {
    const tot = {}; const usr = {};
    (rows || []).forEach((r) => { const o = parseObj(r[field]); Object.keys(o).forEach((k) => { tot[k] = (tot[k] || 0) + o[k]; (usr[k] = usr[k] || new Set()).add(r.user); }); });
    return Object.keys(tot).map((k) => ({ k, n: tot[k], users: usr[k].size })).sort((a, b) => b.n - a.n);
  };
  const toolAgg = useMemo(() => sumJson(data && data.toolsRaw, 'tools'), [data]);
  const attachAgg = useMemo(() => sumJson(data && data.attachRaw, 'attach_types'), [data]);
  const tokUser = useMemo(() => { const m = {}; ((data && data.tokensByUser) || []).forEach((r) => { m[r.user] = r; }); return m; }, [data]);
  const tokTot = useMemo(() => Object.values(tokUser).reduce((t, r) => ({ i: t.i + (r.est_in || 0), o: t.o + (r.est_out || 0) }), { i: 0, o: 0 }), [tokUser]);
  const fmtBytes = (b) => { const n = Number(b) || 0; if (!n) return ''; if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`; if (n >= 1024) return `${Math.round(n / 1024)} KB`; return `${n} B`; };
  const hours = useMemo(() => {
    const m = new Map(((data && data.byHour) || []).map((h) => [h.hour, h.prompts]));
    return Array.from({ length: 24 }, (_, h) => ({ hour: String(h).padStart(2, '0'), prompts: m.get(h) || 0 }));
  }, [data]);

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-gray-800">Import Claude export</div>
            <div className="text-xs text-gray-500">
              Claude Admin settings → Data and privacy → Export data. Step 1: upload the <b>manifest .json</b> from the “Your data is ready” email — download buttons for the two needed files appear below (links work for 24 h, in a browser logged in to claude.ai). Step 2: upload <code>conversations-000.zip</code> + <code>light_metadata-000.zip</code> together (multi-select).
              Only metadata is stored (member, time, message counts, length, conversation title) — message text is discarded.
              Re-importing a newer export updates existing rows.
            </div>
          </div>
          <input ref={fileRef} type="file" accept=".zip,.json,.jsonl" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
          <button onClick={() => fileRef.current && fileRef.current.click()} disabled={busy}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: 'rgb(var(--brand-600))' }}>
            {busy ? 'Importing…' : '⬆ Upload manifest / zip(s)'}
          </button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs">
          <label className="flex cursor-pointer items-center gap-2 font-semibold text-gray-700">
            <input type="checkbox" checked={storeText} onChange={(e) => setStoreText(e.target.checked)} />
            Store message text (confidential)
          </label>
          <span className="text-gray-500">Off = metadata only. On = full text kept for admin reading &amp; search; passwords, tokens, keys and card numbers are masked; every read is logged.</span>
          <span className="ml-auto text-gray-600">{textStat ? `${fmtNum(textStat.n)} texts stored in ${fmtNum(textStat.convs)} conversation(s)` : ''}</span>
          {textStat && textStat.n ? <button onClick={purgeText} className="rounded border border-red-300 bg-white px-2 py-1 font-semibold text-red-600 hover:bg-red-50">Delete all stored text</button> : null}
        </div>
        {last && <div className="mt-2 text-xs text-gray-500">Last import: {fmtTs(last.imported_at)} by {last.imported_by} · {last.file_name} · {fmtNum(last.conversations)} conversations · data {fmtTs(last.min_ts)} → {fmtTs(last.max_ts)}</div>}
        {manifest && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <div className="font-semibold">Manifest loaded (created {manifest.created ? fmtTs(Date.parse(manifest.created)) : '—'}). Download these files, then upload them here together:</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {manifest.files.map((d) => (
                <a key={d.export_url} href={d.export_url} target="_blank" rel="noopener noreferrer"
                  className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 font-mono text-xs font-semibold text-amber-900 hover:bg-amber-100">⬇ {d.filename}</a>
              ))}
            </div>
            <div className="mt-2 text-xs">Each link may work only once — let each download finish completely. If a link fails, request a new export in Organization settings.</div>
          </div>
        )}
        {msg && <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-700">{msg}</div>}
        {error && <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-700">{error}</div>}
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Kpi label="Members" value={fmtNum(k.users)} />
            <Kpi label="Conversations" value={fmtNum(k.conversations)} />
            <Kpi label="Prompts" value={fmtNum(k.human_msgs)} />
            <Kpi label="Replies" value={fmtNum(k.assistant_msgs)} />
            <Kpi label="Attachments" value={fmtNum(k.attachments)} sub={k.last_ts ? `latest msg ${fmtTs(k.last_ts)}` : null} />
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div><div className="text-xs font-medium uppercase tracking-wide text-gray-500">Est. output tokens</div><div className="text-2xl font-bold text-gray-800">{fmtTok(tokTot.o)}</div></div>
              <div><div className="text-xs font-medium uppercase tracking-wide text-gray-500">Est. input tokens (max)</div><div className="text-2xl font-bold text-gray-800">{fmtTok(tokTot.i)}</div></div>
              <div><div className="text-xs font-medium uppercase tracking-wide text-gray-500">API-equivalent cost (with caching)</div><div className="text-2xl font-bold text-gray-800">{fmtUsd(estCost(tokTot.i, tokTot.o))}</div></div>
              <div className="ml-auto flex items-center gap-2 text-xs text-gray-600">
                <span>Price / 1M tokens — input $</span>
                <input type="number" min="0" step="0.1" value={priceIn} onChange={(e) => savePrice('claudeChat.priceIn', Number(e.target.value), setPriceIn)} className="w-16 rounded border border-gray-300 px-2 py-1" />
                <span>output $</span>
                <input type="number" min="0" step="0.1" value={priceOut} onChange={(e) => savePrice('claudeChat.priceOut', Number(e.target.value), setPriceOut)} className="w-16 rounded border border-gray-300 px-2 py-1" />
                <span>cache read %</span>
                <input type="number" min="0" max="100" step="1" value={cachePct} onChange={(e) => savePrice('claudeChat.cachePct', Number(e.target.value), setCachePct)} className="w-14 rounded border border-gray-300 px-2 py-1" />
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Estimate only — the export has no token data. Tokens ≈ characters ÷ {(data && data.charsPerToken) || 4}. Input assumes the conversation history (capped at 200K tokens) is re-sent on every reply;
              that history is priced as prompt-cache reads ({cachePct}% of the input price), and new content is priced once as a cache write (1.25× input) plus output. Set cache read % to 100 for the old no-cache upper bound.
              Chat on the Team plan is billed per seat, so this is not an invoice — use it to compare load between members.
            </div>
          </div>

          <Card title="👥 Members (chat)" right={`${data.byUser.length} member(s)`}>
            <Table head={[{ k: 'Member' }, { k: 'Conversations', r: 1 }, { k: 'Prompts', r: 1 }, { k: 'Replies', r: 1 }, { k: 'Avg prompt chars', r: 1 }, { k: 'Attachments', r: 1 }, { k: 'Est. tokens (in / out)', r: 1 }, { k: 'Est. cost', r: 1 }, { k: 'Active days', r: 1 }, { k: 'Last active' }]}
              rows={data.byUser.map((u) => (
                <tr key={u.user} className="cursor-pointer hover:bg-gray-50" onClick={() => setDetail(u.user)}>
                  <td className="px-3 py-2"><div className="font-medium" style={{ color: 'rgb(var(--brand-700))' }}>{u.name || u.user} ›</div>{u.name && <div className="text-xs text-gray-500">{u.user}</div>}</td>
                  <td className="px-3 py-2 text-right">{fmtNum(u.conversations)}</td>
                  <td className="px-3 py-2 text-right">{fmtNum(u.prompts)}</td>
                  <td className="px-3 py-2 text-right">{fmtNum(u.replies)}</td>
                  <td className="px-3 py-2 text-right">{u.prompts ? fmtNum(u.prompt_chars / u.prompts) : '—'}</td>
                  <td className="px-3 py-2 text-right">{fmtNum(u.attachments)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{tokUser[u.user] ? `${fmtTok(tokUser[u.user].est_in)} / ${fmtTok(tokUser[u.user].est_out)}` : '—'}</td>
                  <td className="px-3 py-2 text-right">{tokUser[u.user] ? fmtUsd(estCost(tokUser[u.user].est_in, tokUser[u.user].est_out)) : '—'}</td>
                  <td className="px-3 py-2 text-right">{u.active_days}</td>
                  <td className="px-3 py-2 text-gray-600">{fmtTs(u.last_ts)}</td>
                </tr>
              ))} empty="No chat data in this period. Upload an export first." />
            <div className="mt-2 text-xs text-gray-400">Click a member for a detailed profile.</div>
          </Card>

          <Card title="🔗 Chat + Cowork combined (no double counting)" right="chat = export · cowork = live telemetry">
            <Table head={[{ k: 'Member' }, { k: 'Chat conv.', r: 1 }, { k: 'Chat typed prompts', r: 1 }, { k: 'Cowork sessions', r: 1 }, { k: 'Cowork prompts', r: 1 },
              { k: 'Total prompts', r: 1 }, { k: 'Chat est. cost', r: 1 }, { k: 'Cowork cost', r: 1 }, { k: 'Total cost', r: 1 }, { k: 'Checks' }]}
              rows={(data.combined || []).sort((x, y) => y.total_prompts - x.total_prompts).map((o) => {
                const chatCost = estCost(o.chat_est_in, o.chat_est_out);
                return (
                  <tr key={o.user}>
                    <td className="px-3 py-2">{shortUser(o.user)}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(o.chat_conversations)}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(o.chat_typed_prompts)}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(o.cowork_sessions)}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(o.cowork_prompts)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmtNum(o.total_prompts)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{fmtUsd(chatCost)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{fmtUsd(o.cowork_cost)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmtUsd(chatCost + o.cowork_cost)}</td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {o.duplicates_removed ? <span className="mr-2 rounded bg-amber-100 px-1.5 py-0.5 text-amber-700">{o.duplicates_removed} dup removed</span> : null}
                      {o.time_overlaps ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-700">{o.time_overlaps} time overlap</span> : null}
                      {!o.duplicates_removed && !o.time_overlaps ? <span className="text-emerald-600">✓ no overlap</span> : null}
                    </td>
                  </tr>
                );
              })} />
            <div className="mt-2 text-xs text-gray-500">
              Export conversations whose ID matches a Cowork session are excluded from the chat side. Chat prompts count only messages with text
              (tool-result turns are skipped) so they compare 1:1 with Cowork <code>user_prompt</code> events. Chat cost is an estimate from text length
              (upper bound); Cowork cost is reported by Claude. Cowork telemetry only exists from the day it was enabled (14 Oct 2026).
            </div>
          </Card>
          {detail && <MemberDetail email={detail} days={days} estCost={estCost} onClose={() => setDetail(null)} onFilter={setUserFilter} />}

          <div className="grid gap-5 lg:grid-cols-2">
            <Card title="📈 Prompts per day">
              {data.byDay.length ? (
                <div style={{ width: '100%', height: 220 }}>
                  <ResponsiveContainer>
                    <BarChart data={data.byDay} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f3" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} width={40} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="prompts" name="Prompts" fill="rgb(var(--brand-600))" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <div className="py-6 text-center text-sm text-gray-400">No data for this period.</div>}
            </Card>
            <Card title="🕘 Prompts by hour (WIB)">
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                  <BarChart data={hours} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f3" />
                    <XAxis dataKey="hour" tick={{ fontSize: 11 }} interval={1} />
                    <YAxis tick={{ fontSize: 11 }} width={40} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="prompts" name="Prompts" fill="rgb(var(--brand-500))" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <Card title="🏷️ Topics by member" right="from conversation titles only">
            <Table head={[{ k: 'Topic' }].concat(topicMatrix.users.map((u) => ({ k: shortUser(u), r: 1 })), [{ k: 'Total', r: 1 }])}
              rows={topicMatrix.topics.map((t) => (
                <tr key={t} className={`cursor-pointer hover:bg-gray-50 ${topicFilter === t ? 'bg-amber-50' : ''}`} onClick={() => setTopicFilter(topicFilter === t ? 'all' : t)}>
                  <td className="px-3 py-2 font-medium text-gray-800">{t}</td>
                  {topicMatrix.users.map((u) => {
                    const v = topicMatrix.cell[`${t}|${u}`];
                    return (
                      <td key={u} className="px-3 py-2 text-right">
                        {v ? <button onClick={(e) => { e.stopPropagation(); setTopicCell({ topic: t, user: u }); }}
                              className="rounded px-1.5 font-medium hover:underline" style={{ color: 'rgb(var(--brand-700))' }} title="Show conversations">{v}</button>
                          : <span className="text-gray-300">0</span>}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right font-semibold">
                    <button onClick={(e) => { e.stopPropagation(); setTopicCell({ topic: t, user: null }); }} className="rounded px-1.5 hover:underline" style={{ color: 'rgb(var(--brand-700))' }} title="Show conversations">{topicMatrix.tTot[t]}</button>
                  </td>
                </tr>
              ))} />
            <div className="mt-2 text-xs text-gray-400">Click a number to list those conversations; click a topic row to filter the conversation list. Topics are assigned by keyword rules on the title.</div>
            {topicCell && (() => {
              const list = (data.conversations || []).filter((c) => (c.topic || 'Untitled') === topicCell.topic && (!topicCell.user || c.user === topicCell.user))
                .sort((a, b) => (b.last_msg_ts || 0) - (a.last_msg_ts || 0));
              return (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setTopicCell(null)}>
                  <div className="flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-start justify-between border-b border-gray-200 px-5 py-3">
                      <div>
                        <div className="font-semibold text-gray-800">🏷️ {topicCell.topic}</div>
                        <div className="text-xs text-gray-500">{topicCell.user || 'All members'} · {list.length} conversation(s) · {fmtNum(list.reduce((a, c) => a + (c.human_msgs || 0), 0))} prompts · est. {fmtUsd(list.reduce((a, c) => a + (c.est_out_tokens == null ? 0 : estCost(c.est_in_tokens, c.est_out_tokens)), 0))}</div>
                      </div>
                      <button onClick={() => setTopicCell(null)} className="rounded p-1 text-gray-500 hover:bg-gray-100">✕</button>
                    </div>
                    <div className="overflow-auto p-3">
                      <Table head={[{ k: 'Last message' }].concat(topicCell.user ? [] : [{ k: 'Member' }], [{ k: 'Title' }, { k: 'Project' }, { k: 'Duration', r: 1 }, { k: 'Prompts', r: 1 }, { k: 'Est. cost', r: 1 }])}
                        rows={list.map((c) => (
                          <tr key={c.uuid} className="cursor-pointer hover:bg-gray-50" onClick={() => { setTopicCell(null); setReadUuid(c.uuid); }}>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-600">{fmtTs(c.last_msg_ts)}</td>
                            {topicCell.user ? null : <td className="px-3 py-2">{shortUser(c.user)}</td>}
                            <td className="px-3 py-2 max-w-xs truncate font-medium" style={{ color: 'rgb(var(--brand-700))' }} title={c.title || ''}>{c.title || '(untitled)'}</td>
                            <td className="px-3 py-2 max-w-[10rem] truncate text-xs text-gray-600">{c.project_name || '—'}</td>
                            <td className="px-3 py-2 text-right">{fmtDur(c.first_msg_ts, c.last_msg_ts)}</td>
                            <td className="px-3 py-2 text-right">{fmtNum(c.human_msgs)}</td>
                            <td className="px-3 py-2 text-right">{c.est_out_tokens == null ? '—' : fmtUsd(estCost(c.est_in_tokens, c.est_out_tokens))}</td>
                          </tr>
                        ))} />
                      {!list.length && <div className="py-6 text-center text-sm text-gray-400">No conversations in the loaded list.</div>}
                      <div className="mt-2 text-xs text-gray-400">Click a conversation to read it.</div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </Card>

          <div className="grid gap-5 lg:grid-cols-3">
            <Card title="🛠️ Tools used by Claude" right={`${toolAgg.length} tool(s)`}>
              <Table head={[{ k: 'Tool' }, { k: 'Calls', r: 1 }, { k: 'Members', r: 1 }]}
                rows={toolAgg.slice(0, 25).map((t) => (
                  <tr key={t.k}><td className="px-3 py-1.5 font-mono text-xs">{t.k}</td><td className="px-3 py-1.5 text-right">{fmtNum(t.n)}</td><td className="px-3 py-1.5 text-right">{t.users}</td></tr>
                ))} empty="No tool data — re-import a new export to fill this." />
            </Card>
            <Card title="📎 Attachment types" right={`${attachAgg.length} type(s)`}>
              <Table head={[{ k: 'Type' }, { k: 'Files', r: 1 }, { k: 'Members', r: 1 }]}
                rows={attachAgg.slice(0, 25).map((t) => (
                  <tr key={t.k}><td className="px-3 py-1.5 font-mono text-xs">{t.k}</td><td className="px-3 py-1.5 text-right">{fmtNum(t.n)}</td><td className="px-3 py-1.5 text-right">{t.users}</td></tr>
                ))} empty="No attachment data — re-import a new export to fill this." />
            </Card>
            <Card title="📁 Projects" right={`${(data.byProject || []).length} project(s)`}>
              <Table head={[{ k: 'Project' }, { k: 'Conv.', r: 1 }, { k: 'Members', r: 1 }]}
                rows={(data.byProject || []).map((p) => (
                  <tr key={p.project}><td className="px-3 py-1.5">{p.project}</td><td className="px-3 py-1.5 text-right">{fmtNum(p.conversations)}</td><td className="px-3 py-1.5 text-right">{p.users}</td></tr>
                ))} empty="No conversations inside Claude projects." />
            </Card>
          </div>

          <Card title="🔎 Search message text" right={textStat && textStat.n ? 'searches stored text only · logged' : 'no text stored yet'}>
            <div className="flex flex-wrap gap-2">
              <input value={sq} onChange={(e) => setSq(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && sq.trim().length >= 3) runSearch(); }}
                placeholder="e.g. ME21N, server, laporan…" className="min-w-[16rem] flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm" disabled={!textStat || !textStat.n} />
              <button onClick={runSearch} disabled={sBusy || sq.trim().length < 3 || !textStat || !textStat.n}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: 'rgb(var(--brand-600))' }}>{sBusy ? 'Searching…' : 'Search'}</button>
            </div>
            {sErr && <div className="mt-2 text-sm text-red-600">{sErr}</div>}
            {sHits && (
              <div className="mt-3 divide-y divide-gray-100">
                {sHits.length ? sHits.map((h) => (
                  <button key={h.uuid} onClick={() => setReadUuid(h.uuid)} className="block w-full px-1 py-2 text-left hover:bg-gray-50">
                    <div className="flex items-center gap-2 text-sm"><span className="font-semibold text-gray-800">{h.title || '(untitled)'}</span>
                      <span className="text-xs text-gray-500">{shortUser(h.user)} · {h.topic} · {h.hits} hit(s) · {fmtTs(h.last_hit)}</span></div>
                    <div className="mt-0.5 truncate text-xs text-gray-600">…{h.snippet}…</div>
                  </button>
                )) : <div className="py-3 text-sm text-gray-400">No matches.</div>}
              </div>
            )}
          </Card>

          <Card title="💬 Conversations" right={
            <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)} className="rounded border border-gray-300 bg-white px-2 py-1 text-xs">
              <option value="all">All members</option>
              {data.byUser.map((u) => <option key={u.user} value={u.user}>{u.name || u.user}</option>)}
            </select>}>
            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-gray-500">Topic:</span>
              <select value={topicFilter} onChange={(e) => setTopicFilter(e.target.value)} className="rounded border border-gray-300 bg-white px-2 py-1">
                <option value="all">All topics</option>
                {topicMatrix.topics.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <span className="text-gray-400">{convs.length} conversation(s)</span>
            </div>
            <Table head={[{ k: 'Last message' }, { k: 'Member' }, { k: 'Title' }, { k: 'Topic' }, { k: 'Project' }, { k: 'Duration', r: 1 }, { k: 'Prompts', r: 1 }, { k: 'Words (user / Claude)', r: 1 }, { k: 'Est. tokens (in / out)', r: 1 }, { k: 'Est. cost', r: 1 }, { k: 'Tools' }, { k: 'Attachments' }]}
              rows={convs.map((c) => (
                <tr key={c.uuid}>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{fmtTs(c.last_msg_ts)}</td>
                  <td className="px-3 py-2">{shortUser(c.user)}</td>
                  <td className="px-3 py-2 max-w-xs truncate" title={c.title || ''}>
                    <button onClick={() => setReadUuid(c.uuid)} className="max-w-full truncate text-left hover:underline" style={{ color: 'rgb(var(--brand-700))' }}>{c.title || '(untitled)'}</button>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap"><span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{c.topic || 'Untitled'}</span></td>
                  <td className="px-3 py-2 max-w-[10rem] truncate text-xs text-gray-600" title={c.project_name || ''}>{c.project_name || '—'}</td>
                  <td className="px-3 py-2 text-right">{fmtDur(c.first_msg_ts, c.last_msg_ts)}</td>
                  <td className="px-3 py-2 text-right">{fmtNum(c.human_msgs)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{c.human_words == null ? '—' : `${fmtNum(c.human_words)} / ${fmtNum(c.assistant_words)}`}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{c.est_out_tokens == null ? '—' : `${fmtTok(c.est_in_tokens)} / ${fmtTok(c.est_out_tokens)}`}</td>
                  <td className="px-3 py-2 text-right">{c.est_out_tokens == null ? '—' : fmtUsd(estCost(c.est_in_tokens, c.est_out_tokens))}</td>
                  <td className="px-3 py-2 max-w-[12rem] truncate font-mono text-xs text-gray-600" title={Object.entries(parseObj(c.tools)).map(([k, v]) => `${k} ×${v}`).join(', ')}>
                    {c.tool_calls ? Object.entries(parseObj(c.tools)).sort((x, y) => y[1] - x[1]).map(([k, v]) => `${k}×${v}`).join(', ') : '—'}
                  </td>
                  <td className="px-3 py-2 max-w-[12rem] truncate text-xs text-gray-600" title={parseArr(c.attach_names).join('\n')}>
                    {c.attachments ? `${fmtNum(c.attachments)} · ${Object.entries(parseObj(c.attach_types)).map(([k, v]) => `${k}×${v}`).join(', ')}${c.attach_bytes ? ` · ${fmtBytes(c.attach_bytes)}` : ''}` : '—'}
                  </td>
                </tr>
              ))} />
          </Card>
        </>
      )}
      {readUuid && <Transcript uuid={readUuid} highlight={sHits ? sq.trim() : ''} onClose={() => setReadUuid(null)} />}
    </div>
  );
}

// Period → query fragment. Custom/month ranges are sent as "365&from=…&to=…" so every
// existing `days=${days}` URL keeps working; the backend uses from/to when present.
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const monthRange = (offset) => { const n = new Date(); const a = new Date(n.getFullYear(), n.getMonth() + offset, 1); const b = new Date(n.getFullYear(), n.getMonth() + offset + 1, 0); return [ymd(a), ymd(b)]; };
export const periodText = (days) => { const m = String(days).match(/from=([\d-]+)&to=([\d-]+)/); return m ? `${m[1]} → ${m[2]}` : `last ${days} days`; };

// Dashboard MCP tools (primary_dashboard): names and error messages from the MCP server's own audit log.
function McpToolsCard({ days }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    api.get(`/api/claude-otel/mcp-tools?days=${days}`)
      .then((r) => live && setD(r.data))
      .catch((e) => live && setErr((e.response && e.response.data && e.response.data.error) || e.message));
    return () => { live = false; };
  }, [days]);
  if (err) return <Card title="🔌 Dashboard MCP tools"><div className="text-sm text-red-600">{err}</div></Card>;
  if (!d) return null;
  const tot = d.byTool.reduce((a, t) => ({ c: a.c + t.calls, f: a.f + t.failed }), { c: 0, f: 0 });
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card title="🔌 Dashboard MCP tools" right={`${fmtNum(tot.c)} calls · ${fmtNum(tot.f)} failed (${tot.c ? ((tot.f / tot.c) * 100).toFixed(1) : 0}%)`}>
        <Table head={[{ k: 'Tool' }, { k: 'Calls', r: 1 }, { k: 'Failed', r: 1 }, { k: 'Fail %', r: 1 }, { k: 'Avg ms', r: 1 }]}
          rows={d.byTool.map((t) => (
            <tr key={t.tool}>
              <td className="px-3 py-2 font-mono text-xs">{t.tool}</td>
              <td className="px-3 py-2 text-right">{fmtNum(t.calls)}</td>
              <td className={`px-3 py-2 text-right ${t.failed ? 'text-red-600' : ''}`}>{fmtNum(t.failed)}</td>
              <td className="px-3 py-2 text-right">{t.calls ? ((t.failed / t.calls) * 100).toFixed(1) : '0.0'}%</td>
              <td className="px-3 py-2 text-right">{fmtNum(t.avg_ms)}</td>
            </tr>
          ))} />
        <div className="mt-2 text-xs text-gray-400">From the primary_dashboard MCP server audit log. Claude telemetry reports these only as "mcp_tool" without names.</div>
      </Card>
      <Card title="⚠️ Top MCP errors" right={`${d.topErrors.length} distinct`}>
        <Table head={[{ k: 'Tool' }, { k: 'Error' }, { k: 'Count', r: 1 }, { k: 'Last' }]}
          rows={d.topErrors.map((e, i) => (
            <tr key={i}>
              <td className="px-3 py-2 font-mono text-xs">{e.tool}</td>
              <td className="px-3 py-2 text-xs text-gray-700 break-all">{e.err}</td>
              <td className="px-3 py-2 text-right">{fmtNum(e.n)}</td>
              <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{fmtTs(e.last_ts * 1000)}</td>
            </tr>
          ))} />
      </Card>
    </div>
  );
}

export default function ClaudeActivity() {
  const [preset, setPreset] = useState('30');
  const [range, setRange] = useState(() => monthRange(0));
  const days = useMemo(() => {
    if (preset === 'this_month') { const r = monthRange(0); return `365&from=${r[0]}&to=${r[1]}`; }
    if (preset === 'last_month') { const r = monthRange(-1); return `365&from=${r[0]}&to=${r[1]}`; }
    if (preset === 'custom') return range[0] && range[1] && range[0] <= range[1] ? `365&from=${range[0]}&to=${range[1]}` : '30';
    return preset;
  }, [preset, range]);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [session, setSession] = useState(null);
  const [events, setEvents] = useState([]);
  const [evLoading, setEvLoading] = useState(false);
  const [metric, setMetric] = useState('prompts');
  const [view, setView] = useState('cowork');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [d, s] = await Promise.all([
        api.get(`/api/claude-otel/dashboard?days=${days}`),
        api.get('/api/claude-otel/status'),
      ]);
      setData(d.data); setStatus(s.data);
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.error) || e.message);
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const openSession = async (s) => {
    setSession(s); setEvents([]); setEvLoading(true);
    try {
      const r = await api.get(`/api/claude-otel/events?days=${days}&session=${encodeURIComponent(s.session_id)}&limit=2000`);
      setEvents((r.data.rows || []).slice().reverse());
    } catch (e) { setEvents([]); } finally { setEvLoading(false); }
  };

  const k = (data && data.kpi) || {};
  const users = useMemo(() => (data ? data.byUser.map((u) => u.user) : []), [data]);
  const sessions = useMemo(() => (data ? data.sessions.filter((s) => userFilter === 'all' || s.user === userFilter) : []), [data, userFilter]);
  const totalTok = (Number(k.input_tokens) || 0) + (Number(k.output_tokens) || 0) + (Number(k.cache_read_tokens) || 0) + (Number(k.cache_creation_tokens) || 0);
  const cacheShare = totalTok ? Math.round(((Number(k.cache_read_tokens) || 0) / totalTok) * 100) : 0;
  const metricLabel = { prompts: 'Prompts', cost: 'Cost (USD)', sessions: 'Sessions', tool_calls: 'Tool calls' };

  const exportCsv = () => {
    if (!data) return;
    const head = ['user', 'sessions', 'prompts', 'tool_calls', 'tokens', 'cost_usd', 'active_days', 'last_active'];
    const rows = data.byUser.map((u) => [u.user, u.sessions, u.prompts, u.tool_calls, u.tokens || 0, (u.cost || 0).toFixed(6), u.active_days, new Date(u.last_ts).toISOString()]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([[head.join(',')].concat(rows).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = `claude-activity-${periodText(days).replace(/[^\d-]+/g, '_')}.csv`; a.click();
    URL.revokeObjectURL(a.href);
  };

  const lastIn = status && status.stats && status.stats.lastReceivedAt;
  const rejected = status && status.stats && status.stats.rejected;

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800">✳️ Claude Activity</h1>
          <p className="text-sm text-gray-500">
            Member activity in Claude (Cowork / unified chat) for the Pacificindomas organization, received over
            OpenTelemetry. Prompt and response text are redacted by Claude — only metadata is shown.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={preset} onChange={(e) => setPreset(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm">
            {DAY_OPTIONS.map((d) => <option key={d} value={String(d)}>Last {d} day{d > 1 ? 's' : ''}</option>)}
            <option value="this_month">This month</option>
            <option value="last_month">Last month</option>
            <option value="custom">Custom range…</option>
          </select>
          {preset === 'custom' && (
            <span className="flex items-center gap-1 text-sm">
              <input type="date" value={range[0]} max={range[1] || undefined} onChange={(e) => setRange([e.target.value, range[1]])} className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm" />
              <span className="text-gray-400">→</span>
              <input type="date" value={range[1]} min={range[0] || undefined} onChange={(e) => setRange([range[0], e.target.value])} className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm" />
            </span>
          )}
          <button onClick={load} disabled={loading} className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: 'rgb(var(--brand-600))' }}>
            {loading ? 'Loading…' : '↻ Refresh'}
          </button>
          <button onClick={exportCsv} disabled={!data} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">⬇ CSV</button>
        </div>
      </div>

      {status && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
          <span>Receiver: <b className="text-emerald-600">online</b> · {status.protocol} · retention {status.retentionDays} days</span>
          <span>Last data received: {lastIn ? fmtTs(lastIn) : 'not since last restart'}</span>
          {rejected ? <span className="text-red-600">Rejected requests since restart: {rejected} (check token in Claude Admin settings)</span> : null}
        </div>
      )}
      <div className="flex gap-1 border-b border-gray-200">
        {[['cowork', 'Cowork (live telemetry)'], ['chat', 'Chat (Export data import)']].map(([v, l]) => (
          <button key={v} onClick={() => setView(v)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${view === v ? 'text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            style={view === v ? { borderColor: 'rgb(var(--brand-600))' } : undefined}>{l}</button>
        ))}
      </div>

      {view === 'chat' && <ChatExportPanel days={days} />}

      {view === 'cowork' && error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {view === 'cowork' && data && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
            <Kpi label="Active members" value={fmtNum(k.users)} />
            <Kpi label="Sessions" value={fmtNum(k.sessions)} />
            <Kpi label="Prompts" value={fmtNum(k.prompts)} />
            <Kpi label="Tool calls" value={fmtNum(k.tool_calls)} sub={k.tool_failures ? `${k.tool_failures} failed` : 'no failures'} />
            <Kpi label="API requests" value={fmtNum(k.requests)} sub={k.api_errors ? `${k.api_errors} errors` : null} />
            <Kpi label="Tokens" value={fmtTok(totalTok)} sub={`${cacheShare}% cache reads`} />
            <Kpi label="Est. cost" value={fmtUsd(k.cost)} sub="list price, from Claude" />
          </div>

          <Card title="📈 Daily trend" right={
            <span className="flex gap-1">
              {Object.keys(metricLabel).map((m) => (
                <button key={m} onClick={() => setMetric(m)}
                  className={`rounded px-2 py-0.5 ${metric === m ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{metricLabel[m]}</button>
              ))}
            </span>}>
            {data.byDay.length ? (
              <div style={{ width: '100%', height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={data.byDay} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f3" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={48} tickFormatter={(v) => (metric === 'cost' ? `$${v}` : fmtTok(v))} />
                    <Tooltip formatter={(v) => (metric === 'cost' ? fmtUsd(v) : fmtNum(v))} />
                    <Bar dataKey={metric} name={metricLabel[metric]} fill="rgb(var(--brand-600))" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <div className="py-6 text-center text-sm text-gray-400">No data for this period.</div>}
          </Card>

          <Card title="👥 Members" right={`${data.byUser.length} member(s) with telemetry`}>
            <Table head={[{ k: 'Member' }, { k: 'Sessions', r: 1 }, { k: 'Prompts', r: 1 }, { k: 'Tool calls', r: 1 }, { k: 'Tokens', r: 1 }, { k: 'Est. cost', r: 1 }, { k: 'Active days', r: 1 }, { k: 'Last active' }]}
              rows={data.byUser.map((u) => (
                <tr key={u.user} className="cursor-pointer hover:bg-gray-50" onClick={() => setUserFilter(u.user)}>
                  <td className="px-3 py-2 font-medium text-gray-800">{u.user}</td>
                  <td className="px-3 py-2 text-right">{fmtNum(u.sessions)}</td>
                  <td className="px-3 py-2 text-right">{fmtNum(u.prompts)}</td>
                  <td className="px-3 py-2 text-right">{fmtNum(u.tool_calls)}</td>
                  <td className="px-3 py-2 text-right">{fmtTok(u.tokens)}</td>
                  <td className="px-3 py-2 text-right">{fmtUsd(u.cost)}</td>
                  <td className="px-3 py-2 text-right">{u.active_days}</td>
                  <td className="px-3 py-2 text-gray-600">{fmtTs(u.last_ts)}</td>
                </tr>
              ))} />
            <div className="mt-2 text-xs text-gray-400">Members appear here after their first Cowork session with telemetry enabled. Click a member to filter sessions.</div>
          </Card>

          {(() => {
            // [Efficiency] derived client-side from byUser + sessions
            const rows = data.byUser.filter((u) => u.prompts > 0).map((u) => {
              const ss = (data.sessions || []).filter((s) => s.user === u.user && s.prompts > 0);
              const opusCost = ss.filter((s) => /opus/.test(s.models || '')).reduce((a, s) => a + (s.cost || 0), 0);
              const sesCost = ss.reduce((a, s) => a + (s.cost || 0), 0) || 0;
              const top = ss.reduce((m, s) => (!m || s.cost > m.cost ? s : m), null);
              const heavyCost = ss.filter((s) => s.prompts >= 50).reduce((a, s) => a + (s.cost || 0), 0);
              return {
                user: u.user,
                cpp: u.cost / u.prompts,
                tpp: u.tool_calls / u.prompts,
                ppd: u.prompts / Math.max(1, u.active_days),
                ppsess: ss.length ? ss.reduce((a, s) => a + s.prompts, 0) / ss.length : 0,
                usedSess: ss.length,
                idleSess: Math.max(0, (u.sessions || 0) - ss.length),
                opusShare: sesCost ? opusCost / sesCost : 0,
                heavyShare: sesCost ? heavyCost / sesCost : 0,
                top,
              };
            });
            const minCpp = Math.min(...rows.map((r) => r.cpp));
            const maxPpd = Math.max(...rows.map((r) => r.ppd));
            rows.forEach((r) => {
              // Model choice is not scored: a stronger model can be cheaper per correct result (less rework).
              r.score = Math.round(40 * (minCpp / r.cpp) + 35 * (1 - r.heavyShare) + 25 * (r.ppd / maxPpd));
            });
            rows.sort((a, b) => b.score - a.score);
            const pct = (v) => `${Math.round(v * 100)}%`;
            return (
              <Card title="⚡ Efficiency" right={(() => {
                const ts = (data.sessions || []).filter((s) => s.prompts > 0);
                if (!ts.length) return `Period: ${periodText(days)}`;
                const toD = (v) => new Date(typeof v === 'number' && v < 1e12 ? v * 1000 : v);
                const a = new Date(Math.min(...ts.map((s) => toD(s.start_ts).getTime())));
                const b = new Date(Math.max(...ts.map((s) => toD(s.end_ts).getTime())));
                const f = (d) => d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                const n = Math.max(1, Math.round((b - a) / 86400000) + 1);
                return `Period: ${periodText(days)} · data ${f(a)} – ${f(b)} (${n} days)`;
              })()}>
                <Table head={[{ k: 'Member' }, { k: 'Score', r: 1 }, { k: 'Cost / prompt', r: 1 }, { k: 'Tools / prompt', r: 1 }, { k: 'Prompts / active day', r: 1 }, { k: 'Prompts / session', r: 1 }, { k: 'Opus cost share', r: 1 }, { k: 'Long-session cost share', r: 1 }, { k: 'Idle sessions', r: 1 }, { k: 'Costliest session', r: 1 }]}
                  rows={rows.map((r) => (
                    <tr key={r.user}>
                      <td className="px-3 py-2 font-medium text-gray-800">{shortUser(r.user)}</td>
                      <td className={`px-3 py-2 text-right font-semibold ${r.score >= 70 ? 'text-green-600' : r.score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{r.score}</td>
                      <td className="px-3 py-2 text-right">{fmtUsd(r.cpp)}</td>
                      <td className="px-3 py-2 text-right">{r.tpp.toFixed(1)}</td>
                      <td className="px-3 py-2 text-right">{r.ppd.toFixed(1)}</td>
                      <td className="px-3 py-2 text-right">{r.ppsess.toFixed(1)}</td>
                      <td className="px-3 py-2 text-right">{pct(r.opusShare)}</td>
                      <td className="px-3 py-2 text-right">{pct(r.heavyShare)}</td>
                      <td className="px-3 py-2 text-right">{fmtNum(r.idleSess)}</td>
                      <td className="px-3 py-2 text-right">{r.top ? `${fmtUsd(r.top.cost)} (${r.top.prompts} prompts)` : '—'}</td>
                    </tr>
                  ))} />
                <div className="mt-2 text-xs text-gray-400">
                  Score (0–100) = 40% cost per prompt (vs. best member) + 35% cost outside long sessions (≥50 prompts, where re-sent context makes every prompt expensive) + 25% prompts per active day (vs. most active). Model choice is shown for information but not scored — a stronger model can cost less per correct result when it avoids rework. Measures efficiency only — prompt content is redacted, so task outcome/quality cannot be measured here. Idle sessions = sessions with no prompt.
                </div>
              </Card>
            );
          })()}

          <McpToolsCard days={days} />

          <div className="grid gap-5 lg:grid-cols-2">
            <Card title="🛠️ Tools" right={`${data.byTool.length} tool(s)`}>
              <Table head={[{ k: 'Tool' }, { k: 'Calls', r: 1 }, { k: 'Failed', r: 1 }, { k: 'Avg ms', r: 1 }, { k: 'Members', r: 1 }]}
                rows={data.byTool.map((t) => (
                  <tr key={t.tool || '-'}>
                    <td className="px-3 py-2 font-mono text-xs">{t.tool || '—'}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(t.calls)}</td>
                    <td className={`px-3 py-2 text-right ${t.failures ? 'text-red-600' : ''}`}>{fmtNum(t.failures)}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(t.avg_ms)}</td>
                    <td className="px-3 py-2 text-right">{t.users}</td>
                  </tr>
                ))} />
            </Card>
            <Card title="🧠 Models" right={`${data.byModel.length} model(s)`}>
              <Table head={[{ k: 'Model' }, { k: 'Requests', r: 1 }, { k: 'Tokens', r: 1 }, { k: 'Est. cost', r: 1 }, { k: 'Avg ms', r: 1 }]}
                rows={data.byModel.map((m) => (
                  <tr key={m.model || '-'}>
                    <td className="px-3 py-2 font-mono text-xs">{m.model || '—'}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(m.requests)}</td>
                    <td className="px-3 py-2 text-right">{fmtTok(m.tokens)}</td>
                    <td className="px-3 py-2 text-right">{fmtUsd(m.cost)}</td>
                    <td className="px-3 py-2 text-right">{fmtNum(m.avg_ms)}</td>
                  </tr>
                ))} />
            </Card>
          </div>

          <Card title="🗂️ Sessions" right={
            <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)} className="rounded border border-gray-300 bg-white px-2 py-1 text-xs">
              <option value="all">All members</option>
              {users.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>}>
            <Table head={[{ k: 'Last activity' }, { k: 'Member' }, { k: 'Duration', r: 1 }, { k: 'Prompts', r: 1 }, { k: 'Tool calls', r: 1 }, { k: 'Est. cost', r: 1 }, { k: 'Models' }, { k: '' }]}
              rows={sessions.map((s) => (
                <tr key={s.session_id + s.user} className={`hover:bg-gray-50 ${session && session.session_id === s.session_id ? 'bg-amber-50' : ''}`}>
                  <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{fmtTs(s.end_ts)}</td>
                  <td className="px-3 py-2">{shortUser(s.user)}</td>
                  <td className="px-3 py-2 text-right">{fmtDur(s.start_ts, s.end_ts)}</td>
                  <td className="px-3 py-2 text-right">{fmtNum(s.prompts)}</td>
                  <td className="px-3 py-2 text-right">{fmtNum(s.tool_calls)}</td>
                  <td className="px-3 py-2 text-right">{fmtUsd(s.cost)}</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-500">{s.models || '—'}</td>
                  <td className="px-3 py-2 text-right"><button onClick={() => openSession(s)} className="text-xs font-semibold" style={{ color: 'rgb(var(--brand-700))' }}>Timeline →</button></td>
                </tr>
              ))} />
          </Card>

          {session && (
            <Card title={`🕒 Session timeline — ${shortUser(session.user)}`} right={
              <span className="flex items-center gap-3">
                <span className="font-mono">{session.session_id}</span>
                <button onClick={() => setSession(null)} className="text-gray-500 hover:text-gray-800">✕ Close</button>
              </span>}>
              {evLoading ? <div className="py-6 text-center text-sm text-gray-500">Loading events…</div> : (
                <Table head={[{ k: 'Time' }, { k: 'Event' }, { k: 'Detail' }]}
                  rows={events.map((e) => (
                    <tr key={e.id}>
                      <td className="px-3 py-1.5 whitespace-nowrap text-gray-600">{fmtTs(e.ts)}</td>
                      <td className="px-3 py-1.5 font-mono text-xs">{e.event_name}</td>
                      <td className="px-3 py-1.5 text-xs text-gray-600">{eventDetail(e)}</td>
                    </tr>
                  ))} empty="No events found for this session in the selected period." />
              )}
            </Card>
          )}

          <Card title="🏷️ Event types" right={`${data.byEvent.length} type(s)`}>
            <div className="flex flex-wrap gap-2">
              {data.byEvent.map((e) => (
                <span key={e.event} className="rounded-full bg-gray-100 px-2.5 py-1 font-mono text-xs text-gray-700">{e.event} <b>{fmtNum(e.n)}</b></span>
              ))}
            </div>
          </Card>
        </>
      )}
      {view === 'cowork' && loading && !data && <div className="rounded-lg border bg-white p-6 text-center text-sm text-gray-500">Loading Claude telemetry…</div>}
    </div>
  );
}
